/**
 * Product Media Toggle – "Fotos / Maße" switch for the product gallery (A/B test)
 * Markup + settings: snippets/product-media-toggle.liquid
 *
 * Sits on top of the existing gallery (snippets/product-images-bc.liquid) without changing it:
 * - the toggle and the Maße image are mounted inside the main slider (#ProductPhotosSlider-{productId})
 * - the thumbnail strip mirrors the slides of the main slider and drives it through its Embla instance
 *
 * Event for tracking: document 'pmt:view-change' → detail { view, productId, variantId, userAction }
 */
(function () {
  'use strict';

  if (window.__productMediaToggleLoaded) return;
  window.__productMediaToggleLoaded = true;

  var MOBILE_QUERY = window.matchMedia('(max-width: 768px)');
  var SHOPIFY_CDN = /\/cdn\/shop\/|cdn\.shopify\.com\//;
  var STAGE_WIDTHS = [600, 900, 1200, 1600, 2000];
  var THUMB_WIDTHS = [120, 180, 240, 360];

  function sizedUrl(src, width) {
    if (!src || !SHOPIFY_CDN.test(src)) return src;
    try {
      var url = new URL(src, window.location.href);
      url.searchParams.set('width', width);
      url.searchParams.delete('height');
      url.searchParams.delete('crop');
      return url.href;
    } catch (e) {
      return src;
    }
  }

  function srcsetFor(src, widths) {
    if (!src || !SHOPIFY_CDN.test(src)) return '';
    return widths
      .map(function (width) {
        return sizedUrl(src, width) + ' ' + width + 'w';
      })
      .join(', ');
  }

  function firstSrcsetUrl(srcset) {
    return srcset ? srcset.split(',')[0].trim().split(' ')[0] : '';
  }

  function onMediaChange(query, handler) {
    if (query.addEventListener) query.addEventListener('change', handler);
    else if (query.addListener) query.addListener(handler);
  }

  class ProductMediaToggle {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-pmt-config]').textContent);
      this.variants = this.config.variants || {};
      this.variantId = String(this.config.variantId);
      this.view = 'fotos';
      this.activeIndex = -1;
      this.signature = null;
      this.preloaded = {};

      var scope = root.closest('.product-section') || document;
      var productId = this.config.productId;
      this.gallery = scope.querySelector('.product_media__original.product__photos-' + productId);
      this.slider = scope.querySelector('#ProductPhotosSlider-' + productId);
      if (!this.gallery || !this.slider) return;

      this.mountUi();
      this.applySizeFallback();
      this.bindUi();
      this.initThumbs();
      this.placeFlowToggle();
      this.bindVariantEvents();
      this.bindEditorEvents();
      this.updateAvailability();

      if (this.config.defaultView === 'masse') this.setView('masse', false);
    }

    /* ---------- Setup ---------- */

    mountUi() {
      var template = this.root.querySelector('template[data-pmt-ui]');
      var ui = template.content.cloneNode(true);

      this.toggle = ui.querySelector('[data-pmt-toggle]');
      this.stage = ui.querySelector('[data-pmt-stage]');
      this.stageImg = this.stage.querySelector('[data-pmt-img]');

      // Mobile copy below the image (already in the page flow, rendered by the snippet)
      this.flowToggle = this.root.querySelector('.pmt-toggle--flow');
      this.toggles = [this.toggle, this.flowToggle].filter(Boolean);
      this.buttons = [];
      this.toggles.forEach(function (toggle) {
        this.buttons = this.buttons.concat(Array.prototype.slice.call(toggle.querySelectorAll('[data-pmt-view]')));
      }, this);
      this.masseButtons = this.buttons.filter(function (button) {
        return button.getAttribute('data-pmt-view') === 'masse';
      });

      // clever-slider is position: relative – all layers sit on top of the slides
      this.slider.appendChild(this.stage);
      var corners = ui.querySelector('[data-pmt-corners]');
      if (corners) this.slider.appendChild(corners);
      this.slider.appendChild(this.toggle);

      if (!this.config.showArrows) this.gallery.classList.add('pmt-opt-hide-arrows');
      if (this.config.hideBadges) this.gallery.classList.add('pmt-opt-hide-badges');
    }

    // The desktop size rules use a CSS style query (only true for --pmt-ab: block). Browsers
    // without style queries do not set the --pmt-sq probe – they get the same rules via a class.
    applySizeFallback() {
      var sticky = this.root.closest('.product-single__sticky');
      if (!sticky) return;
      var supported = window.getComputedStyle(this.root).getPropertyValue('--pmt-sq').trim() === '1';
      var newLook = window.getComputedStyle(this.stage).display !== 'none';
      sticky.classList.toggle('pmt-sq-fallback', !supported && newLook);
    }

    bindUi() {
      var self = this;

      this.buttons.forEach(function (button) {
        button.addEventListener('click', function () {
          self.setView(button.getAttribute('data-pmt-view'));
        });
      });

      this.masseButtons.forEach(function (button) {
        ['pointerenter', 'touchstart', 'focus'].forEach(function (eventName) {
          button.addEventListener(eventName, self.preload.bind(self), { passive: true });
        });
      });

      // Slider arrows and the original thumbnails bring the photos back
      this.gallery.addEventListener(
        'click',
        function (event) {
          if (self.view !== 'masse') return;
          if (event.target.closest('cs-prev, cs-next, clever-slider[for] cs-slide')) self.setView('fotos');
        },
        true
      );

      if (this.config.swipeBack) this.bindSwipe();

      onMediaChange(MOBILE_QUERY, function () {
        if (!self.canShowMasse()) self.setView('fotos', false);
        self.updateThumbArrows();
      });
    }

    bindSwipe() {
      var self = this;
      var startX = null;
      var startY = null;

      this.stage.addEventListener(
        'touchstart',
        function (event) {
          startX = event.touches[0].clientX;
          startY = event.touches[0].clientY;
        },
        { passive: true }
      );

      this.stage.addEventListener(
        'touchend',
        function (event) {
          if (startX === null) return;
          var deltaX = event.changedTouches[0].clientX - startX;
          var deltaY = event.changedTouches[0].clientY - startY;
          startX = null;
          if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) self.setView('fotos');
        },
        { passive: true }
      );
    }

    bindVariantEvents() {
      var self = this;
      var handler = function (event) {
        var detail = event.detail || {};
        var id = detail.variant ? detail.variant.id : detail.variant_id;
        if (id) self.setVariant(id);
      };
      document.addEventListener('variant:change', handler);
      document.addEventListener('update:variant:image', handler);
    }

    bindEditorEvents() {
      if (!window.Shopify || !window.Shopify.designMode) return;
      var self = this;
      document.addEventListener('shopify:block:select', function (event) {
        if (event.detail && event.detail.blockId === self.config.blockId) self.setView('masse', false);
      });
      document.addEventListener('shopify:block:deselect', function (event) {
        if (event.detail && event.detail.blockId === self.config.blockId) self.setView('fotos', false);
      });
    }

    /* ---------- Fotos / Maße ---------- */

    current() {
      return this.variants[this.variantId] || null;
    }

    // The "Show on" setting and the CSS switch (--pmt-ab: none) both hide the stage with display: none
    canShowMasse() {
      return !!this.current() && window.getComputedStyle(this.stage).display !== 'none';
    }

    setView(view, userAction) {
      if (view === 'masse' && !this.canShowMasse()) view = 'fotos';
      if (view === this.view) return;

      var isMasse = view === 'masse';
      this.view = view;

      this.buttons.forEach(function (button) {
        var active = button.getAttribute('data-pmt-view') === view;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      if (isMasse) this.showImage();
      this.stage.classList.toggle('is-active', isMasse);
      this.stage.setAttribute('aria-hidden', isMasse ? 'false' : 'true');
      this.gallery.classList.toggle('pmt-is-masse', isMasse);

      document.dispatchEvent(
        new CustomEvent('pmt:view-change', {
          detail: {
            view: view,
            productId: this.config.productId,
            variantId: this.variantId,
            userAction: userAction !== false,
          },
        })
      );
    }

    imageAttributes(data) {
      return {
        src: data.src,
        srcset: data.srcset || srcsetFor(data.src, STAGE_WIDTHS),
        sizes: '(min-width: 769px) 50vw, 100vw',
      };
    }

    showImage() {
      var data = this.current();
      if (!data || this.stageImg.getAttribute('data-src') === data.src) return;

      var img = this.stageImg;
      var stage = this.stage;
      var attrs = this.imageAttributes(data);
      var done = function () {
        stage.classList.remove('is-loading');
      };

      stage.classList.add('is-loading');
      img.onload = done;
      img.onerror = done;
      img.setAttribute('data-src', data.src);
      img.alt = data.alt || '';
      img.sizes = attrs.sizes;
      if (attrs.srcset) img.srcset = attrs.srcset;
      else img.removeAttribute('srcset');
      img.src = attrs.src;
      if (img.complete && img.naturalWidth) done();
    }

    preload() {
      var data = this.current();
      if (!data || this.preloaded[data.src]) return;
      this.preloaded[data.src] = true;

      var attrs = this.imageAttributes(data);
      var img = new Image();
      img.sizes = attrs.sizes;
      if (attrs.srcset) img.srcset = attrs.srcset;
      img.src = attrs.src;
    }

    setVariant(id) {
      id = String(id);
      if (!Object.prototype.hasOwnProperty.call(this.variants, id) || id === this.variantId) return;

      this.variantId = id;
      this.updateAvailability();

      if (this.view !== 'masse') return;
      if (this.config.resetOnVariant || !this.current()) this.setView('fotos', false);
      else this.showImage();
    }

    updateAvailability() {
      var hasImage = !!this.current();
      var hide = !hasImage && this.config.noImage !== 'disable';
      this.masseButtons.forEach(function (button) {
        button.disabled = !hasImage;
      });
      this.toggles.forEach(function (toggle) {
        toggle.hidden = hide;
      });
    }

    // Keep the mobile switch right below the thumbnails (or the gallery), like the snippet renders it
    placeFlowToggle() {
      if (!this.flowToggle) return;
      var anchor = this.thumbs && this.thumbs.parentNode ? this.thumbs : this.gallery;
      if (anchor.nextElementSibling !== this.flowToggle) anchor.insertAdjacentElement('afterend', this.flowToggle);
    }

    /* ---------- Thumbnails below the big image ---------- */

    initThumbs() {
      var self = this;
      this.thumbs = this.config.thumbs ? this.root.querySelector('[data-pmt-thumbs]') : null;
      this.sliderTrack = this.slider.querySelector('cs-track');
      if (!this.thumbs || !this.sliderTrack) return;

      // Directly below the gallery, before anything apps inject after it (e.g. #mbcBundleItems)
      this.gallery.insertAdjacentElement('afterend', this.thumbs);

      this.track = this.thumbs.querySelector('[data-pmt-thumbs-track]');
      this.prevButton = this.thumbs.querySelector('[data-pmt-thumbs-prev]');
      this.nextButton = this.thumbs.querySelector('[data-pmt-thumbs-next]');

      this.track.addEventListener('click', function (event) {
        var thumb = event.target.closest('.pmt-thumb[data-index]');
        if (thumb) self.goTo(Number(thumb.getAttribute('data-index')));
      });

      if (this.prevButton && this.nextButton) {
        this.prevButton.addEventListener('click', function () {
          self.scrollThumbs(-1);
        });
        this.nextButton.addEventListener('click', function () {
          self.scrollThumbs(1);
        });
        this.track.addEventListener('scroll', this.updateThumbArrows.bind(this), { passive: true });
        if (window.ResizeObserver) new ResizeObserver(this.updateThumbArrows.bind(this)).observe(this.track);
      }

      // The existing gallery replaces slides on variant change and lazily adds more after load,
      // and clever-slider marks the current slide with .is-selected – follow both.
      // (Mutation records are already batched per task, no extra debounce needed.)
      var observer = new MutationObserver(this.renderThumbs.bind(this));
      observer.observe(this.sliderTrack, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'src', 'srcset', 'data-src'],
      });

      this.renderThumbs();
    }

    getSlides() {
      var container = this.sliderTrack.querySelector('cs-slides');
      if (!container) return [];
      return Array.prototype.filter.call(container.children, function (element) {
        return element.localName === 'cs-slide';
      });
    }

    slideInfo(slide) {
      var img = slide.querySelector('img');
      var src = slide.getAttribute('data-thumbnail-src') || '';
      if (!src && img) {
        src =
          img.getAttribute('src') ||
          img.getAttribute('data-src') ||
          firstSrcsetUrl(img.getAttribute('srcset') || img.getAttribute('data-srcset'));
      }
      return {
        src: src,
        isVideo: !!slide.querySelector('video, .product__video-wrapper, model-viewer, [data-product-media-type-model]'),
      };
    }

    renderThumbs() {
      var infos = this.getSlides().map(this.slideInfo, this);
      if (!infos.length) return;

      var signature = infos
        .map(function (info) {
          return info.src + (info.isVideo ? '#video' : '');
        })
        .join('|');

      if (signature !== this.signature) {
        this.signature = signature;
        this.activeIndex = -1;

        var fragment = document.createDocumentFragment();
        infos.forEach(function (info, index) {
          fragment.appendChild(this.createThumb(info, index, infos.length));
        }, this);

        this.track.replaceChildren(fragment);
        this.thumbs.hidden = infos.length < 2;
      }

      this.syncActiveThumb();
      this.updateThumbArrows();
    }

    createThumb(info, index, total) {
      var thumb = document.createElement('button');
      thumb.type = 'button';
      thumb.className = 'pmt-thumb' + (info.isVideo ? ' pmt-thumb--video' : '');
      thumb.setAttribute('data-index', index);
      thumb.setAttribute(
        'aria-label',
        (this.config.thumbLabel || '%1 / %2').replace('%1', index + 1).replace('%2', total)
      );

      if (info.src) {
        var img = document.createElement('img');
        var srcset = srcsetFor(info.src, THUMB_WIDTHS);
        img.className = 'pmt-thumb__img';
        img.alt = '';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.draggable = false;
        if (srcset) {
          img.srcset = srcset;
          img.sizes = '(min-width: 769px) 160px, 25vw';
        }
        img.src = sizedUrl(info.src, 240);
        thumb.appendChild(img);
      }

      if (info.isVideo) {
        var play = document.createElement('span');
        play.className = 'pmt-thumb__play';
        play.setAttribute('aria-hidden', 'true');
        thumb.appendChild(play);
      }

      return thumb;
    }

    syncActiveThumb() {
      var index = this.getSlides().findIndex(function (slide) {
        return slide.classList.contains('is-selected');
      });
      this.setActiveThumb(index < 0 ? 0 : index);
    }

    setActiveThumb(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;

      var thumbs = this.track.children;
      for (var i = 0; i < thumbs.length; i++) {
        var active = i === index;
        thumbs[i].classList.toggle('is-active', active);
        if (active) thumbs[i].setAttribute('aria-current', 'true');
        else thumbs[i].removeAttribute('aria-current');
      }
      this.scrollThumbIntoView(thumbs[index]);
    }

    scrollThumbIntoView(thumb) {
      if (!thumb) return;
      var track = this.track;
      var start = thumb.offsetLeft;
      var end = start + thumb.offsetWidth;

      // Scroll the strip only – never the page
      if (start < track.scrollLeft) {
        track.scrollTo({ left: start, behavior: 'smooth' });
      } else if (end > track.scrollLeft + track.clientWidth) {
        track.scrollTo({ left: end - track.clientWidth, behavior: 'smooth' });
      }
    }

    goTo(index) {
      this.setView('fotos');
      this.setActiveThumb(index);
      // Read the instance on every click: clever-slider re-creates Embla when the gallery is moved
      var embla = this.slider.embla;
      if (embla && typeof embla.scrollTo === 'function') embla.scrollTo(index);
    }

    scrollThumbs(direction) {
      this.track.scrollBy({ left: direction * this.track.clientWidth * 0.8, behavior: 'smooth' });
    }

    updateThumbArrows() {
      if (!this.prevButton || !this.nextButton) return;
      var track = this.track;
      var maxScroll = track.scrollWidth - track.clientWidth;
      this.thumbs.classList.toggle('has-overflow', maxScroll > 2);
      this.prevButton.disabled = track.scrollLeft <= 2;
      this.nextButton.disabled = track.scrollLeft >= maxScroll - 2;
    }
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-pmt]:not([data-pmt-ready])');
    Array.prototype.forEach.call(roots, function (root) {
      root.setAttribute('data-pmt-ready', '');
      try {
        new ProductMediaToggle(root);
      } catch (error) {
        console.error('[product-media-toggle]', error);
      }
    });
  }

  window.ProductMediaToggle = ProductMediaToggle;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
